/**
 * 解析 PikeRag v2 SSE final.answer 字段。
 *
 * 后端模型有时会把回答以 JSON 字符串的形式塞进 answer 字段里，形如：
 *   {
 *     "answer": "真正展示给用户的文字",
 *     "rationale": "需要展示在 Summary 区域的推理"
 *   }
 *
 * 也可能再被 Markdown 代码围栏（```json ... ```）包裹，
 * 并且模型经常会在字符串值中混入未转义的英文/中文双引号，导致严格 JSON.parse 失败。
 *
 * 本工具提供两种粒度：
 *  - parseFinalAnswer(raw)            ：用于"流结束后"的最终解析，识别失败返回 null
 *  - extractStreamingAnswerView(buf)  ：用于"流式过程中"的实时展示
 */
export interface ParsedAnswer {
  answer: string;
  rationale: string;
}

/** 去掉外层的 ```json ... ``` 或 ``` ... ``` 代码围栏（允许未闭合，便于流式中使用） */
export function stripCodeFence(text: string): string {
  const trimmed = text.trim();
  // 已经闭合的代码围栏
  const closed = trimmed.match(/^```(?:json)?\s*([\s\S]*?)\s*```$/i);
  if (closed) return closed[1].trim();
  // 仅有开头的代码围栏（流式途中常见）
  const openOnly = trimmed.match(/^```(?:json)?\s*([\s\S]*)$/i);
  if (openOnly) return openOnly[1].trim();
  return trimmed;
}

/** 仅做基础反转义（\n \r \t \" \\），不强求完整 JSON 字符串语义 */
export function unescapeBasic(s: string): string {
  // 单次扫描的状态机，避免 `\\\\n` 被先按 `\\n` 吃掉导致结果变成
  // 字面反斜杠 + 真换行（在 markdown 中行尾反斜杠会转义后续换行）。
  let out = "";
  for (let i = 0; i < s.length; i++) {
    const c = s[i];
    if (c !== "\\") {
      out += c;
      continue;
    }
    const next = s[i + 1];
    if (next === undefined) {
      // 末尾孤立反斜杠，保留原样（调用者会做 HOLDBACK）
      out += "\\";
      continue;
    }
    switch (next) {
      case "n":
        out += "\n";
        break;
      case "r":
        // \r\n 折叠为 \n
        if (s[i + 2] === "\\" && s[i + 3] === "n") {
          out += "\n";
          i += 2;
        } else {
          out += "\r";
        }
        break;
      case "t":
        out += "\t";
        break;
      case '"':
        out += '"';
        break;
      case "\\":
        out += "\\";
        break;
      case "/":
        out += "/";
        break;
      default:
        // 未识别转义保留原 \X，避免破坏内容
        out += "\\" + next;
        break;
    }
    i += 1;
  }
  return out;
}

/**
 * 兜底解析：当严格 JSON.parse 失败时，按 "answer" / "rationale" 两个 key 的边界
 * 直接做字符串切片。可以容忍 value 中包含未转义的双引号。
 *
 * 仅识别两种结构：
 *   { "answer": "...", "rationale": "..." }
 *   { "answer": "..." }
 */
function regexExtract(text: string): ParsedAnswer | null {
  const answerKey = /"answer"\s*:\s*"/;
  const answerKeyMatch = text.match(answerKey);
  if (!answerKeyMatch || answerKeyMatch.index === undefined) return null;
  const answerValueStart = answerKeyMatch.index + answerKeyMatch[0].length;

  // 在 answer 之后查找 rationale key
  const rationaleKeyRegex = /,\s*"rationale"\s*:\s*"/g;
  rationaleKeyRegex.lastIndex = answerValueStart;
  const rationaleKeyMatch = rationaleKeyRegex.exec(text);

  let answerValueRaw: string;
  let rationaleValueRaw = "";

  if (rationaleKeyMatch && rationaleKeyMatch.index !== undefined) {
    // answer 的字符串值结束于紧贴 `,\s*"rationale"` 之前的最后一个未转义双引号
    const between = text.substring(answerValueStart, rationaleKeyMatch.index);
    const closingIdx = findLastUnescapedQuote(between);
    if (closingIdx < 0) return null;
    answerValueRaw = between.substring(0, closingIdx);

    const rationaleValueStart = rationaleKeyMatch.index + rationaleKeyMatch[0].length;
    const afterRationale = text.substring(rationaleValueStart);
    // rationale 的字符串值结束于 `"\s*}` 处的最后一个未转义双引号
    const endQuoteIdx = findLastUnescapedQuoteBeforeBrace(afterRationale);
    rationaleValueRaw =
      endQuoteIdx >= 0 ? afterRationale.substring(0, endQuoteIdx) : afterRationale;
  } else {
    // 仅有 answer 字段
    const remaining = text.substring(answerValueStart);
    const endQuoteIdx = findLastUnescapedQuoteBeforeBrace(remaining);
    if (endQuoteIdx < 0) return null;
    answerValueRaw = remaining.substring(0, endQuoteIdx);
  }

  return {
    answer: unescapeBasic(answerValueRaw),
    rationale: unescapeBasic(rationaleValueRaw),
  };
}

/** 在 [...,]\s*"rationale" 之前找最近的、且不是 \" 的双引号位置 */
function findLastUnescapedQuote(s: string): number {
  for (let i = s.length - 1; i >= 0; i--) {
    if (s[i] !== '"') continue;
    // 计算前面连续反斜杠的数量
    let backslashes = 0;
    let j = i - 1;
    while (j >= 0 && s[j] === "\\") {
      backslashes++;
      j--;
    }
    if (backslashes % 2 === 0) return i;
  }
  return -1;
}

/** 找到最后一个紧跟 `}` 的未转义双引号位置 */
function findLastUnescapedQuoteBeforeBrace(s: string): number {
  // 从右到左找 `}` 后的第一个 `"`
  const closeBraceIdx = s.lastIndexOf("}");
  const searchEnd = closeBraceIdx >= 0 ? closeBraceIdx : s.length;
  const slice = s.substring(0, searchEnd);
  return findLastUnescapedQuote(slice);
}

export function parseFinalAnswer(raw: unknown): ParsedAnswer | null {
  if (raw == null) return null;

  // 后端如果直接给对象，直接读字段
  if (typeof raw === "object") {
    const obj = raw as Record<string, unknown>;
    const ans = obj.answer;
    if (typeof ans === "string") {
      return {
        answer: ans,
        rationale: typeof obj.rationale === "string" ? obj.rationale : "",
      };
    }
    return null;
  }

  if (typeof raw !== "string") return null;
  const text = raw.trim();
  if (!text) return null;
  if (!text.includes("{")) return null;

  const candidate = stripCodeFence(text);
  if (!candidate.startsWith("{")) return null;

  // 1) 先尝试严格 JSON.parse
  if (candidate.endsWith("}")) {
    try {
      const parsed = JSON.parse(candidate);
      if (parsed && typeof parsed === "object" && typeof (parsed as any).answer === "string") {
        return {
          answer: (parsed as any).answer,
          rationale:
            typeof (parsed as any).rationale === "string" ? (parsed as any).rationale : "",
        };
      }
    } catch {
      /* 落到 regex 兜底 */
    }
  }

  // 2) 兜底：按 key 边界做正则提取，容忍 value 内的未转义双引号
  return regexExtract(candidate);
}

/**
 * 流式过程中根据已经累积的 buffer 计算"应该展示给用户的回答文本"。
 *
 * - 如果 buffer 看起来不是 JSON（即模型直接输出纯文本），原样返回；
 * - 如果 buffer 看起来是 JSON 但 "answer" 字段还没出现，返回空串（等待）；
 * - 如果已经看到 "answer": " ，提取截至当前的 value 部分（可能还在生成中）。
 */
export function extractStreamingAnswerView(buffer: string): string {
  if (!buffer) return "";
  const trimmedStart = buffer.replace(/^\s+/, "");
  const looksLikeJson =
    trimmedStart.startsWith("{") || /^```(?:json)?/i.test(trimmedStart);
  if (!looksLikeJson) return buffer;

  const stripped = stripCodeFence(buffer);

  const answerKeyMatch = stripped.match(/"answer"\s*:\s*"/);
  if (!answerKeyMatch || answerKeyMatch.index === undefined) {
    // JSON 已开头但还没生成到 answer 字段，回退到空串避免直接展示 `{`
    return "";
  }
  const valueStart = answerKeyMatch.index + answerKeyMatch[0].length;
  const rest = stripped.substring(valueStart);

  // 看是否已经看到 ",\s*"rationale" 边界（answer 已结束）
  const rationaleBoundary = rest.match(/"\s*,\s*"rationale"/);
  if (rationaleBoundary && rationaleBoundary.index !== undefined) {
    return unescapeBasic(rest.substring(0, rationaleBoundary.index));
  }

  // 或者已经看到对象结束 `"\s*}` （只有 answer 字段的情况）
  const objectEnd = rest.match(/"\s*}\s*$/);
  if (objectEnd && objectEnd.index !== undefined) {
    return unescapeBasic(rest.substring(0, objectEnd.index));
  }

  // 还在流式生成 answer 的中段，直接展示当前已生成的部分（去掉末尾未闭合的引号）
  // 简化处理：直接返回剩余字符串去掉行尾引号
  return unescapeBasic(rest);
}

/**
 * 二级队列：在收到上游 content delta 时，识别 plain / json 两种模式，
 * 仅把"用户应当看到的字符（answer 内的明文）"以 delta 形式吐出，
 * 让外层 typewriter 按帧节奏渲染，避免越过 `"answer":"` 边界时一次性蹦出整段。
 *
 * 使用：
 *   const filter = createAnswerStreamFilter();
 *   onContent: (delta) => {
 *     for (const piece of filter.push(delta)) typewriter.push(piece);
 *   }
 *   // 流结束
 *   for (const piece of filter.flush()) typewriter.push(piece);
 */
export type AnswerStreamFilter = {
  push: (delta: string) => string[];
  flush: () => string[];
  /** 累积的原始上游 buffer（供最终 final 视图兜底使用） */
  getRawBuffer: () => string;
};

/** 流式解析所处的形态：尚未判定 / 纯文本 / JSON 包裹 */
type StreamMode = "unknown" | "plain" | "json";

export function createAnswerStreamFilter(): AnswerStreamFilter {
  let raw = "";
  let mode: StreamMode = "unknown";
  // raw 中下一个待发射的位置
  let cursor = 0;
  // 是否已找到 "answer":" 起始位置
  let answerStarted = false;
  // 是否已发现 answer 结束边界（rationale 或 }），之后丢弃所有字符
  let answerEnded = false;

  // 保留尾部若干字符不发，防止恰好踩在边界正则中段（最长 `"\s*,\s*"rationale"` 约 16 字符）
  const HOLDBACK = 24;

  const JSON_FALLBACK_THRESHOLD = 200;

  /**
   * 探测 JSON 的 answer 键。
   *   "started"       已定位到 answer 起始，可继续按 JSON 收字
   *   "pending"       还没等到 answer 键，继续攒 buffer
   *   "fellBackPlain" 攒够阈值仍没等到，已把 mode 回落为 plain
   *
   * 回落会改写外层 mode，故用返回值显式告知调用方——
   * 若只返回 boolean，调用方读 mode 时 TS 的控制流分析看不到这次跨函数赋值。
   */
  const tryStartJson = (): "started" | "pending" | "fellBackPlain" => {
    if (answerStarted) return "started";
    const m = raw.match(/"answer"\s*:\s*"/);
    if (!m || m.index === undefined) {
      if (raw.length > JSON_FALLBACK_THRESHOLD) {
        mode = "plain";
        cursor = 0;
        return "fellBackPlain";
      }
      return "pending";
    }
    answerStarted = true;
    cursor = m.index + m[0].length;
    return "started";
  };

  const collectFromCursor = (final: boolean): string[] => {
    if (answerEnded) {
      cursor = raw.length;
      return [];
    }
    const tail = raw.slice(cursor);
    if (!tail) return [];
    let boundary = -1;
    const rm = tail.match(/"\s*,\s*"rationale"/);
    if (rm && rm.index !== undefined) boundary = rm.index;
    const em = tail.match(/"\s*}\s*$/);
    if (em && em.index !== undefined && (boundary < 0 || em.index < boundary)) {
      boundary = em.index;
    }
    let endRel: number;
    if (boundary >= 0) {
      endRel = boundary;
      answerEnded = true;
    } else if (final) {
      // 流结束但没探到边界：把剩余全发，并去掉末尾未闭合的引号
      endRel = tail.replace(/"\s*$/, "").length;
    } else {
      // JSON 模式下只能靠 HOLDBACK 保护跨 chunk 的边界检测，不能用 lastNL 短路：
      // 模型常把 `{"answer":"...内容",\n"rationale":"..."}` 拆在两个 chunk 里，
      // 若沿 `\n` 提前把 tail 全部吐出，闭合 `"` 与 `,` 就会进到正文，导致
      // 下一 chunk 的 `"rationale":...` 因前置 `,` 已被吃掉而匹配不上边界，
      // 整段 rationale 会被当作 answer 显示。
      endRel = Math.max(0, tail.length - HOLDBACK);
    }
    if (endRel <= 0) return [];
    // 末尾孤立反斜杠不要切：会让 `\` 与下一段的 `n` 被切开，导致字面 \n。
    if (!final && endRel > 0 && tail[endRel - 1] === "\\") {
      endRel -= 1;
      if (endRel <= 0) return [];
    }
    const segment = tail.slice(0, endRel);
    cursor += segment.length;
    const decoded = unescapeBasic(segment);
    return decoded ? [decoded] : [];
  };

  // plain 模式：同样跑一次 unescape，兜住模型偶尔在纯文本里写字面 `\n` 的情况；
  // 真换行不受影响（unescapeBasic 只动 `\` 引导的转义序列）。
  // 同时也对末尾孤立反斜杠做 holdback，避免 `\` 与下一段的 `n` 被切开。
  const emitPlain = (final: boolean): string[] => {
    let end = raw.length;
    if (!final && end > cursor && raw[end - 1] === "\\") end -= 1;
    if (end <= cursor) return [];
    const piece = raw.slice(cursor, end);
    cursor = end;
    const decoded = unescapeBasic(piece);
    return decoded ? [decoded] : [];
  };

  return {
    push(delta: string): string[] {
      if (!delta) return [];
      raw += delta;
      if (mode === "unknown") {
        const trimmed = raw.replace(/^\s+/, "");
        if (!trimmed) return [];
        if (trimmed.startsWith("{") || /^```(?:json)?/i.test(trimmed)) {
          mode = "json";
        } else {
          mode = "plain";
          return emitPlain(false);
        }
      }
      if (mode === "plain") {
        return emitPlain(false);
      }
      // json 模式
      const probe = tryStartJson();
      if (probe === "fellBackPlain") return emitPlain(false);
      if (probe === "pending") return [];
      return collectFromCursor(false);
    },
    flush(): string[] {
      if (mode === "plain") {
        return emitPlain(true);
      }
      if (mode === "json") {
        // 收尾阶段无论是否等到 answer 键，都要把已攒内容吐出，不能静默丢弃
        if (tryStartJson() !== "started") return emitPlain(true);
        return collectFromCursor(true);
      }
      // unknown：当作 plain 吐出
      // unknown：当作 plain 吐出
      return emitPlain(true);
    },
    getRawBuffer: () => raw,
  };
}
